import React, { useEffect, useMemo, useRef, useState } from "react";
import { alpha, Box, Chip, ClickAwayListener, Divider, MenuItem, MenuList, Paper, Popper, styled, TextField, Theme, Typography } from "@mui/material";
import { PopperPlacementType } from "@mui/material/Popper/BasePopper.types";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { DotLegalButton, DotLegalHeader, DotLegalPrettyScrollbar } from "@dotlegal/dotlegal-ui-components";
import { FieldGroupingType, FilterOption, FilterOptionType, needsRoomForOpenCalendar } from "./FilterPopover.types.ts";
import SelectFieldSubMenu from "./subMenues/SelectFieldSubMenu";
import RelatedEntitySubMenu from "./subMenues/RelatedEntitySubMenu.tsx";
import FilterPopoverSubMenu from "./FilterPopoverSubMenu.tsx";
import { useTranslation } from "../../../localization/useTranslation.ts";
import BooleanSubMenu from "./subMenues/BooleanSubMenu.tsx";
import DateFieldSubMenu from "./subMenues/DateFieldSubMenu.tsx";
import { hasDateRange } from "@common/components/filterBar/filterOperatorMatching.ts";
import AmountFieldSubMenu from "./subMenues/AmountFieldSubMenu.tsx";
import NumberFieldSubMenu from "./subMenues/NumberFieldSubMenu.tsx";
import RecurrenceFieldSubMenu from "./subMenues/RecurrenceFieldSubMenu.tsx";
import TextFieldSubMenu from "./subMenues/TextFieldSubMenu.tsx";
import CurrencySubMenu from "./subMenues/CurrencySubMenu.tsx";
import FilterPopoverSubMenuEmptyState from "./FilterPopoverSubMenuEmptyState.tsx";
import Skeleton from "@mui/material/Skeleton";
import FilterIcon from "./filter-icon.svg?react";
import { listPageToolbarIconButton, listPageToolbarIconButtonSize } from "@common/components/listPageToolbar/ListPageToolbar.styles";
import CheckIcon from "@mui/icons-material/Check";
import { GetAmountData, GetBooleanData, GetDateData, GetListData, GetNumberData, GetRecurrenceData } from "./subMenues/SubMenuDataHelper.ts";
import { IdNamePair } from "../../../customLists/customListView/CustomListView.types.ts";
import { IdNameWithEntityType } from "../filterBar/IdNameWithEntityType.ts";
import { hasFilterValue as optionHoldsValue } from "../filterBar/filterPinning.ts";

// Mounting a submenu fetches that row's options, so a pass-through hover must not mount one: sweeping the mouse down
// the list would otherwise fire one request per row. The row highlight itself still follows the mouse instantly.
const submenuHoverIntentMs = 150;

// Styled components
export const StyledTextField = styled(TextField)(({ theme }: { theme: Theme }) => ({
    "& .MuiOutlinedInput-root": {
        backgroundColor: theme.customPalette.white,
        borderColor: theme.palette.grey[400],
        fontSize: "14px",
        "&.Mui-focused": {
            backgroundColor: theme.palette.background.paper,
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
                borderWidth: "2px",
            },
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.grey[400],
        },
    },
    "& .MuiInputBase-input": {
        padding: "8px 14px",
        fontSize: theme.typography.pxToRem(14),
    },
}));

export const StyledFilterPaper = styled(Paper)(({ theme }: { theme: Theme }) => ({
    marginTop: theme.spacing(0.5),
    borderRadius: "8px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    minWidth: "240px",
    overflow: "auto",
    paddingLeft: 8,
    paddingRight: 0, // 0px because prettyscrollbar has showgutter true
    "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: theme.palette.grey[100],
    },
    "& .MuiMenuItem-root:hover": {
        backgroundColor: theme.palette.grey[100],
    },
    "& .MuiMenuItem-root.Mui-selected:hover": {
        backgroundColor: theme.palette.grey[100],
    },
}));

export const TextFieldWrapper = styled(Box)(({ theme }: { theme: Theme }) => ({
    padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
}));

export interface FilterPopoverProps {
    isLoading: boolean;
    filterData: Array<FilterOption>;
    onMenuClose: (filterOptions: Array<FilterOption>, pinnedFilterIds: Array<string>) => void;
    filterBarData: Record<string, IdNamePair[]>;
    groupingOrder?: Array<FieldGroupingType>;
    // Ids of filters the user put in the filter bar without giving them a value yet. Seeded from the bar on every
    // open and handed back on close, so clicking a menu row can add or remove a chip the same way emptying one does.
    pinnedFilterIds?: Array<string>;
    // Controlled mode: when `open`, `anchorEl`, and `onOpenChange` are all provided, the
    // component skips its internal "Filter" button + ClickAwayListener and lets the parent
    // drive open state and dismissal. Used by the AI chat scope picker so the `+` button
    // can directly open this popover instead of duplicating its UI.
    open?: boolean;
    anchorEl?: HTMLElement | null;
    onOpenChange?: (open: boolean) => void;
    submenuPlacement?: PopperPlacementType;
}

function FilterPopover(props: FilterPopoverProps) {
    const { translateString } = useTranslation();

    const isControlled = props.open !== undefined && props.onOpenChange !== undefined;

    const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>(props.filterData);
    const [dataFromFilterBar, setDataFromFilterBar] = useState<Record<string, Array<IdNamePair>>>(props.filterBarData);
    const [pinnedIds, setPinnedIds] = useState<Array<string>>(props.pinnedFilterIds ?? []);

    const [mainInputValue, setMainInputValue] = useState("");

    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? props.open! : internalOpen;
    const setOpen = (val: boolean) => {
        if (isControlled) props.onOpenChange!(val);
        else setInternalOpen(val);
    };

    const [submenuOpen, setSubmenuOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // This useEffect is to update the states when customInputFields is done loading
    useEffect(() => {
        if (open) {
            setFilterOptions(props.filterData);
            setDataFromFilterBar(props.filterBarData);
            setPinnedIds(props.pinnedFilterIds ?? []);
        }
        // eslint-disable-next-line
    }, [props.isLoading, open]);

    const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

    const mainInputRef = useRef<HTMLInputElement>(null);
    const submenuInputRef = useRef<HTMLInputElement>(null);
    // DotLegalTextField types its inputRef for a textarea, so the value inputs need their own ref
    // rather than the one the list-based submenus share.
    const submenuTextFieldRef = useRef<HTMLTextAreaElement>();
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuItemRefs = useRef<Map<string, HTMLElement>>(new Map());
    const hoverIntentTimer = useRef<ReturnType<typeof setTimeout>>();

    const cancelPendingSubmenuOpen = () => {
        clearTimeout(hoverIntentTimer.current);
        hoverIntentTimer.current = undefined;
    };

    useEffect(() => () => clearTimeout(hoverIntentTimer.current), []);

    // Group and filter main menu items
    const groupedFilterItems = useMemo(() => {
        const filtered = filterOptions.filter((item) => item.label.toLowerCase().includes(mainInputValue.toLowerCase()));

        if (!props.groupingOrder || props.groupingOrder.length === 0) {
            return [{ grouping: null as FieldGroupingType | null, items: filtered }];
        }

        const groups = new Map<string, FilterOption[]>();
        for (const item of filtered) {
            const key = item.grouping!;
            if (!groups.has(key)) {
                groups.set(key, []);
            }
            groups.get(key)!.push(item);
        }

        return props.groupingOrder.filter((g) => groups.has(g)).map((g) => ({ grouping: g as FieldGroupingType | null, items: groups.get(g)! }));
    }, [filterOptions, mainInputValue, props.groupingOrder]);

    const flatFilteredItems = useMemo(() => groupedFilterItems.flatMap((g) => g.items), [groupedFilterItems]);

    const selectedOption = filterOptions.find((item) => item.id === activeSubmenu);

    const submenuNeedsRoomForCalendar = needsRoomForOpenCalendar(selectedOption);

    // Get the current active descendant ID
    const getActiveDescendantId = () => {
        if (!open) return undefined;

        if (highlightedIndex >= 0) {
            return `main-option-${highlightedIndex}`;
        }

        return undefined;
    };

    // Helper function to determine if a filter has a value
    const hasFilterValue = (item: FilterOption): boolean => {
        switch (item.type) {
            case FilterOptionType.SelectField: {
                const listData = GetListData(item.value);
                if (listData.length === 0) {
                    return false;
                }

                if (item.entityType) {
                    const filterBarDataForOption = dataFromFilterBar[item.id];
                    if (filterBarDataForOption) {
                        return filterBarDataForOption.filter((x) => listData.includes(x.id)).length > 0;
                    }
                }

                return listData.length > 0;
            }

            case FilterOptionType.RelatedEntityField: {
                const listData = GetListData(item.value);
                return listData.length > 0;
            }

            case FilterOptionType.Currency: {
                const listData = GetListData(item.value);
                return listData.length > 0;
            }

            case FilterOptionType.Boolean: {
                const boolData = GetBooleanData(item.value);
                return boolData !== undefined;
            }

            case FilterOptionType.TextField: {
                return typeof item.value === "string" && item.value.trim() !== "";
            }

            case FilterOptionType.DateField: {
                return hasDateRange(GetDateData(item.value));
            }

            case FilterOptionType.Amount: {
                const amountData = GetAmountData(item.value);
                return !!(amountData.from || amountData.to || amountData.currencyCode);
            }

            case FilterOptionType.Number: {
                const numberData = GetNumberData(item.value);
                return !!(numberData.from || numberData.to);
            }

            case FilterOptionType.RecurrenceField: {
                const recurrenceData = GetRecurrenceData(item.value);
                return !!(recurrenceData.from || recurrenceData.to || recurrenceData.recurrenceFilterEnum);
            }

            default:
                return false;
        }
    };

    const getFilterCount = (item: FilterOption): number => {
        if (item.type === FilterOptionType.SelectField) {
            const listData = GetListData(item.value);
            if (item.fixedData && item.fixedData.length > 0) {
                return item.fixedData.filter((x) => listData.includes(x.id)).length;
            }

            // Use data from the hook for SelectField without fixedData
            // For custom fields, the response uses GUID as key, so extract it
            let dataKey = item.id;
            if (item.id.startsWith("custom-field-")) {
                dataKey = item.id.replace("custom-field-", "");
            }

            const hookData = dataFromFilterBar?.[dataKey];

            return listData
                .map((id) => {
                    const foundMatch = hookData?.find((x) => x.id === id);
                    return foundMatch ? { id: id, name: foundMatch.name } : null;
                })
                .filter((x) => x !== null).length;
        }

        if (item.type === FilterOptionType.Currency) {
            return GetListData(item.value).length;
        }

        if (item.type === FilterOptionType.RelatedEntityField) {
            return GetListData(item.value).length;
        }
        return 0;
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, source: "main" | "submenu") => {
        if (!open && e.key === "ArrowDown" && source === "main") {
            setOpen(true);
            setHighlightedIndex(0);
            e.preventDefault();
            return;
        }

        if (!open) return;

        switch (e.key) {
            case "ArrowDown": {
                e.preventDefault();

                const newIndex = highlightedIndex < flatFilteredItems.length - 1 ? highlightedIndex + 1 : highlightedIndex;
                const currentItem = flatFilteredItems[newIndex];

                const itemRef = menuItemRefs.current.get(currentItem.id);
                itemRef?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });

                openSubmenu(currentItem.id, newIndex, false);
                setHighlightedIndex(newIndex);

                break;
            }

            case "ArrowUp": {
                e.preventDefault();

                const newIndex = highlightedIndex > 0 ? highlightedIndex - 1 : highlightedIndex;
                const currentItem = flatFilteredItems[newIndex];

                const itemRef = menuItemRefs.current.get(currentItem.id);
                itemRef?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });

                openSubmenu(currentItem.id, newIndex, false);
                setHighlightedIndex(newIndex);
                break;
            }

            case "ArrowRight":
                e.preventDefault();
                if (highlightedIndex >= 0) {
                    const currentItem = flatFilteredItems[highlightedIndex];
                    openSubmenu(currentItem.id, highlightedIndex, true);
                }
                break;

            case "Enter":
                e.preventDefault();
                if (highlightedIndex >= 0) {
                    const currentItem = flatFilteredItems[highlightedIndex];
                    handleMainItemClick(highlightedIndex, currentItem);
                }

                break;

            case "Escape":
            case "Tab":
                e.preventDefault();
                handleClose();

                break;
        }
    };

    const openSubmenu = (itemId: string, index: number, addFocus: boolean = true) => {
        cancelPendingSubmenuOpen();
        setActiveSubmenu(itemId);
        setSubmenuOpen(true);

        if (addFocus) {
            focusSubmenuInput();
        }
    };

    const focusSubmenuInput = () => {
        setTimeout(() => (submenuInputRef.current ?? submenuTextFieldRef.current)?.focus(), 50);
    };

    const handleClose = () => {
        cancelPendingSubmenuOpen();
        setOpen(false);
        setSubmenuOpen(false);
        setHighlightedIndex(-1);
        setActiveSubmenu(null);
        props.onMenuClose(filterOptions, pinnedIds);
    };

    // Clicking a row puts the filter in the bar with no value yet; clicking it again takes it back out. A filter that
    // already holds a value is in the bar because of that value, so the menu click has nothing to toggle - removing it
    // here would silently discard the user's selections, which is what the chip's X is for. The guard uses the same
    // predicate as nextPinnedFilterIds rather than the type-switched one below, so the two cannot disagree about
    // which filters are empty.
    //
    // A chip parked this way is a deliberate edit of the view, so it is left to count as a change and keeps the reset
    // and update-view actions available. The spurious reset button of #7000 came from the blob a menu close writes -
    // the operator map and the valueless entries - which comparableFilterData normalises away, not from the pins.
    const toggleFilterInBar = (item: FilterOption) => {
        if (optionHoldsValue(item.value)) {
            return;
        }

        setPinnedIds((previous) => (previous.includes(item.id) ? previous.filter((id) => id !== item.id) : [...previous, item.id]));
    };

    const handleMainItemClick = (index: number, item: FilterOption) => {
        setHighlightedIndex(index);
        toggleFilterInBar(item);
        openSubmenu(item.id, index);
    };

    const handleMainItemHover = (index: number, itemId: string) => {
        setHighlightedIndex(index);
        cancelPendingSubmenuOpen();

        if (activeSubmenu === itemId) {
            return;
        }

        // The submenu that is already open stays until the timer swaps it, so moving between rows never flickers.
        // Hovering does not focus the submenu input; only a deliberate click or key press does.
        hoverIntentTimer.current = setTimeout(() => openSubmenu(itemId, index, false), submenuHoverIntentMs);
    };

    // Handle mouse enter on main menu paper
    const handleMainMenuPaperEnter = () => {
        mainInputRef.current?.focus();
    };

    // Handle mouse enter on submenu paper
    const handleSubmenuPaperEnter = () => {
        focusSubmenuInput();
    };

    // Update highlighted indices when filtering
    useEffect(() => {
        if (highlightedIndex >= flatFilteredItems.length) {
            setHighlightedIndex(flatFilteredItems.length > 0 ? 0 : -1);
        }
    }, [highlightedIndex, flatFilteredItems]);

    // Auto-focus search input when popover opens
    useEffect(() => {
        if (open) {
            setTimeout(() => mainInputRef.current?.focus(), 50);
        }
    }, [open]);

    const onCloseSubMenu = () => {
        cancelPendingSubmenuOpen();
        setSubmenuOpen(false);
        setActiveSubmenu(null);
        mainInputRef.current?.focus();
    };

    const setSelectedOptionValue = (value: any) => {
        const updatedOptions = [...filterOptions];
        const foundOption = updatedOptions.find((x) => x.id === selectedOption!.id);
        if (foundOption) {
            foundOption.value = value;
        }
        setFilterOptions(updatedOptions);
    };

    const applyFromSubmenu = (value: any) => {
        setSelectedOptionValue(value);
        handleClose();
    };

    const getCorrectSubmenu = () => {
        switch (selectedOption?.type) {
            case FilterOptionType.SelectField: {
                let dataKey = selectedOption.id;
                if (selectedOption.id.startsWith("custom-field-")) {
                    dataKey = selectedOption.id.replace("custom-field-", "");
                }
                return (
                    <SelectFieldSubMenu
                        filterBarData={dataFromFilterBar[dataKey]}
                        submenuInputRef={submenuInputRef}
                        selectedOption={selectedOption}
                        onToggleSelect={(ids, data) => {
                            setSelectedOptionValue(ids);

                            dataFromFilterBar[dataKey] = data;
                            setDataFromFilterBar(dataFromFilterBar);

                            submenuInputRef.current?.focus();
                        }}
                        onLeftArrowClick={() => onCloseSubMenu()}
                    />
                );
            }
            case FilterOptionType.RelatedEntityField:
                return (
                    <RelatedEntitySubMenu
                        filterBarData={dataFromFilterBar[selectedOption.id] as Array<IdNameWithEntityType> | undefined}
                        submenuInputRef={submenuInputRef}
                        selectedOption={selectedOption}
                        onToggleSelect={(ids, data) => {
                            setSelectedOptionValue(ids);

                            dataFromFilterBar[selectedOption.id] = data;
                            setDataFromFilterBar(dataFromFilterBar);

                            submenuInputRef.current?.focus();
                        }}
                    />
                );
            case FilterOptionType.Boolean:
                return (
                    <BooleanSubMenu
                        submenuInputRef={submenuInputRef}
                        selectedOption={selectedOption}
                        onChange={(val) => setSelectedOptionValue(val)}
                        onLeftArrowClick={() => onCloseSubMenu()}
                    />
                );
            case FilterOptionType.DateField:
                return (
                    <DateFieldSubMenu
                        submenuInputRef={submenuInputRef}
                        selectedOption={selectedOption}
                        onChange={(val) => setSelectedOptionValue(val)}
                        onLeftArrowClick={() => onCloseSubMenu()}
                    />
                );
            case FilterOptionType.Amount:
                return (
                    <AmountFieldSubMenu
                        submenuInputRef={submenuInputRef}
                        selectedOption={selectedOption}
                        onChange={(val) => setSelectedOptionValue(val)}
                        onLeftArrowClick={() => onCloseSubMenu()}
                    />
                );
            case FilterOptionType.Number:
                return (
                    <NumberFieldSubMenu
                        submenuInputRef={submenuTextFieldRef}
                        selectedOption={selectedOption}
                        onChange={(val) => setSelectedOptionValue(val)}
                        onApply={(val) => applyFromSubmenu(val)}
                        onLeftArrowClick={() => onCloseSubMenu()}
                    />
                );
            case FilterOptionType.RecurrenceField:
                return (
                    <RecurrenceFieldSubMenu
                        submenuInputRef={submenuInputRef}
                        selectedOption={selectedOption}
                        onChange={(val) => setSelectedOptionValue(val)}
                        onLeftArrowClick={() => onCloseSubMenu()}
                    />
                );

            case FilterOptionType.TextField:
                return (
                    <TextFieldSubMenu
                        submenuInputRef={submenuTextFieldRef}
                        selectedOption={selectedOption}
                        onChange={(val) => setSelectedOptionValue(val)}
                        onApply={(val) => applyFromSubmenu(val)}
                        onLeftArrowClick={() => onCloseSubMenu()}
                    />
                );

            case FilterOptionType.Currency:
                return (
                    <CurrencySubMenu
                        submenuInputRef={submenuInputRef}
                        selectedOption={selectedOption}
                        onChange={(currencies) => setSelectedOptionValue(currencies)}
                        onLeftArrowClick={() => onCloseSubMenu()}
                    />
                );
        }
    };

    const onFilterButtonClick = () => {
        if (open) {
            handleClose();
        } else {
            setOpen(!open);
        }
    };

    const popoverBody = (
        <Box ref={containerRef}>
            {!isControlled && (
                <Box sx={listPageToolbarIconButton}>
                    <DotLegalButton
                        inputRef={buttonRef}
                        btnLength={listPageToolbarIconButtonSize}
                        buttonType="secondary"
                        onClick={onFilterButtonClick}
                        toolTip={translateString("filter")}
                    >
                        <FilterIcon width={16} height={16} />
                    </DotLegalButton>
                </Box>
            )}

            {/* Main Menu Popover */}
            <Popper open={open} anchorEl={isControlled ? (props.anchorEl ?? null) : buttonRef.current} placement="bottom-start" style={{ zIndex: 1299 }}>
                <StyledFilterPaper sx={{ minWidth: 320, padding: 1, paddingRight: 0.25 }} onMouseEnter={handleMainMenuPaperEnter}>
                    <Box sx={{ ml: -1, mt: -1 }}>
                        <StyledTextField
                            inputRef={mainInputRef}
                            fullWidth
                            value={mainInputValue}
                            onChange={(e) => {
                                setMainInputValue(e.target.value);
                                onCloseSubMenu();
                            }}
                            onKeyDown={(e) => handleKeyDown(e, "main")}
                            placeholder={`${translateString("search")}...`}
                            autoComplete="off"
                            size="small"
                            variant={"standard"}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        role: "combobox",
                                        "aria-expanded": open,
                                        "aria-haspopup": "listbox",
                                        "aria-controls": "main-listbox",
                                        "aria-activedescendant": getActiveDescendantId(),
                                        "aria-autocomplete": "list",
                                    },
                                    disableUnderline: true,
                                },
                            }}
                        />
                        <Divider />
                    </Box>
                    {flatFilteredItems.length === 0 ? (
                        <FilterPopoverSubMenuEmptyState emptyText={translateString("noFilterResultsText")} />
                    ) : (
                        <DotLegalPrettyScrollbar
                            showGutter
                            onScroll={() => {
                                if (submenuOpen) {
                                    onCloseSubMenu();
                                }
                            }}
                            onWheel={() => {
                                if (submenuOpen) {
                                    onCloseSubMenu();
                                }
                            }}
                        >
                            <MenuList id="main-listbox" role="listbox" aria-label="Categories" sx={{ maxHeight: 600 }} onMouseLeave={cancelPendingSubmenuOpen}>
                                {(() => {
                                    let flatIndex = 0;
                                    return groupedFilterItems.map((group) => (
                                        <React.Fragment key={group.grouping ?? "flat-list"}>
                                            {group.grouping && (
                                                <Box sx={{ px: 1 }}>
                                                    <DotLegalHeader headerStyle="small" marginTop={1} marginBottom={1}>
                                                        {translateString(group.grouping)}
                                                    </DotLegalHeader>
                                                </Box>
                                            )}
                                            {group.items.map((item) => {
                                                const currentIndex = flatIndex++;
                                                const isHighlighted = highlightedIndex === currentIndex;
                                                const isList =
                                                    item.type === FilterOptionType.SelectField ||
                                                    item.type === FilterOptionType.Currency ||
                                                    item.type === FilterOptionType.RelatedEntityField;
                                                const isInFilterBar = hasFilterValue(item) || pinnedIds.includes(item.id);
                                                const listCount = isList ? getFilterCount(item) : 0;

                                                return (
                                                    <MenuItem
                                                        ref={(el) => {
                                                            if (el) {
                                                                menuItemRefs.current.set(item.id, el);
                                                            } else {
                                                                menuItemRefs.current.delete(item.id);
                                                            }
                                                        }}
                                                        key={item.id}
                                                        id={`main-option-${currentIndex}`}
                                                        role="option"
                                                        aria-selected={isHighlighted}
                                                        selected={isHighlighted}
                                                        onMouseEnter={() => handleMainItemHover(currentIndex, item.id)}
                                                        onClick={() => handleMainItemClick(currentIndex, item)}
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            fontSize: "14px",
                                                            px: 1,
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <Typography variant="body2">{item.label}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            {isInFilterBar && (
                                                                <Box sx={{ marginLeft: 1 }}>
                                                                    {listCount > 0 ? (
                                                                        <Chip
                                                                            color={"primary"}
                                                                            label={listCount}
                                                                            size="small"
                                                                            sx={{
                                                                                height: "20px",
                                                                                fontSize: "11px",
                                                                                ml: 1,
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <Chip
                                                                            color={"primary"}
                                                                            label={
                                                                                <CheckIcon
                                                                                    sx={(theme) => ({
                                                                                        height: 16,
                                                                                        width: 16,
                                                                                        fill: theme.customPalette.white,
                                                                                    })}
                                                                                />
                                                                            }
                                                                            size="small"
                                                                            sx={{
                                                                                height: "20px",
                                                                                "& .MuiChip-label": {
                                                                                    p: "3px",
                                                                                    height: "22px",
                                                                                },
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            )}
                                                            <ChevronRightIcon fontSize="small" sx={{ fontSize: "18px", color: "text.secondary" }} />
                                                        </Box>
                                                    </MenuItem>
                                                );
                                            })}
                                        </React.Fragment>
                                    ));
                                })()}
                            </MenuList>
                        </DotLegalPrettyScrollbar>
                    )}
                </StyledFilterPaper>
            </Popper>

            {/* Submenu Popover */}
            <FilterPopoverSubMenu
                open={submenuOpen}
                submenuAnchorRef={selectedOption ? menuItemRefs.current.get(selectedOption!.id) : undefined}
                handleSubmenuPaperEnter={handleSubmenuPaperEnter}
                placement={props.submenuPlacement}
                paperSx={submenuNeedsRoomForCalendar ? { overflow: "visible" } : undefined}
            >
                {getCorrectSubmenu()}
            </FilterPopoverSubMenu>
        </Box>
    );

    if (isControlled) {
        return <Box>{popoverBody}</Box>;
    }

    return (
        <Box>
            {props.isLoading ? (
                <Skeleton variant={"rounded"} width={100} height={36} />
            ) : (
                <ClickAwayListener onClickAway={open ? handleClose : () => {}}>{popoverBody}</ClickAwayListener>
            )}
        </Box>
    );
}

export default FilterPopover;
